import React, { useState } from 'react';
import { FC } from 'react';
import { FlexColumn } from '../../basic-flex/flex-column';
import { DistriwinAllAnimalsHeader } from './distriwin-all-animals-header';
import { DistriwinAllAnimalsListSetting, IDistriwinAllAnimalsListSetting, WorkListType } from 'shared/api/api';
import { useDispatch, useSelector } from 'react-redux';
import { WebAppState } from 'web/state/store.web';
import { ViewWeb } from '../../utils/web-view';
import { TextWeb } from 'web/web-helpers/styled-text-components';
import { SkioldFormsWrapper } from '../../skiold-components/skiold-forms-wrapper/skiold-forms-wrapper';
import { FormRow } from '../../skiold-components/skiold-forms-wrapper/skiold-forms-wrapper-types';
import { localized } from 'shared/state/i18n/i18n';
import { getAvailablityRow } from '../work-list-helper';
import { SkioldButton } from '../../skiold-components/skiold-button/skiold-button';
import { SaveWorkListSetting } from 'shared/state/ducks/work-list-settings/operations';
import { SkioldCheckbox } from '../../skiold-components/skiold-checkbox/skiold-checkbox';

interface Props {
	workListType: WorkListType;
}

export const DistriwinAllAnimalsListSetup: FC<Props> = React.memo(({ workListType }) => {
	const dispatch = useDispatch();
	// Setup from redux
	const listSetting = useSelector(
		(state: WebAppState) =>
			state.workListSettings.entities.find(wl => wl.type === workListType) as IDistriwinAllAnimalsListSetting,
	);
	const siteId = useSelector((state: WebAppState) => state.profile.active!.siteId);

	// Init setting
	const [distriwnAllAnimalsSettings, setDistriwnAllAnimalsSettings] = useState<IDistriwinAllAnimalsListSetting>(
		listSetting
			? listSetting
			: DistriwinAllAnimalsListSetting.fromJS({
					type: WorkListType.DistriwinAllAnimalsListSetting,
					siteId: siteId,
			  }),
	);
	const getHeader = (): FormRow => {
		return {
			header: localized(workListType),
		};
	};

	const toggleShowOnWeb = () => {
		setDistriwnAllAnimalsSettings({
			...distriwnAllAnimalsSettings,
			showOnWeb: !distriwnAllAnimalsSettings.showOnWeb,
		} as IDistriwinAllAnimalsListSetting);
	};

	const toggleShowOnApp = () => {
		setDistriwnAllAnimalsSettings({
			...distriwnAllAnimalsSettings,
			showOnApp: !distriwnAllAnimalsSettings.showOnApp,
		} as IDistriwinAllAnimalsListSetting);
	};

	const setNegativeCycleDaysToFalse = () => {
		setDistriwnAllAnimalsSettings({
			...distriwnAllAnimalsSettings,
			negativeCycleDays: false,
		} as IDistriwinAllAnimalsListSetting);
	};

	const setNegativeCycleDaysToTrue = () => {
		setDistriwnAllAnimalsSettings({
			...distriwnAllAnimalsSettings,
			negativeCycleDays: true,
		} as IDistriwinAllAnimalsListSetting);
	};

	const getAvailablityRow = (): FormRow => {
		return {
			name: localized('listAvailableOn'),
			component: (
				<ViewWeb className="work-list-helper-availability-row">
					<SkioldCheckbox onClick={toggleShowOnApp} isChecked={distriwnAllAnimalsSettings.showOnApp!} />
					<TextWeb>{localized('App')}</TextWeb>
					<SkioldCheckbox onClick={toggleShowOnWeb} isChecked={distriwnAllAnimalsSettings.showOnWeb!} />
					<TextWeb>{localized('Web')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getNegativeCycleDaysRow = (): FormRow => {
		return {
			name: localized('SendNegativeCycleDaysForPregnant'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={setNegativeCycleDaysToTrue}
						isChecked={distriwnAllAnimalsSettings.negativeCycleDays}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={setNegativeCycleDaysToFalse}
						isChecked={!distriwnAllAnimalsSettings.negativeCycleDays}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getFormRows = () => {
		let formRows = new Array<FormRow>();
		formRows.push(getHeader());
		formRows.push(getNegativeCycleDaysRow());
		formRows.push(getAvailablityRow());

		return formRows;
	};

	const save = () => {
		dispatch(SaveWorkListSetting(distriwnAllAnimalsSettings));
	};

	const renderButtons = () => {
		return (
			<ViewWeb className="view-button-container">
				<SkioldButton title={localized('Save')} onPress={save} />
			</ViewWeb>
		);
	};

	return (
		<ViewWeb className="list-setup">
			<ViewWeb className="view-container">
				<TextWeb className="text-style">{localized('Setup')}</TextWeb>
				<SkioldFormsWrapper formRows={getFormRows()} containerClassName="wrapper-container" />
				{renderButtons()}
			</ViewWeb>
		</ViewWeb>
	);
});
